import BigCursor from "@/components/CustomCursor";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from "@/components/ui/menubar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useLogo } from "@/context/LogoContext";
import useCustomFontSize from "@/hooks/useCustomFontSize";
import useLanguageContext from "@/hooks/useLanguageContext";
import { cn } from "@/lib/utils";
import { useGetECitizenReadQuery } from "@/services/Redux/Features/ECitizenSlice";
import { useLazyGetSearchByTextQuery } from "@/services/Redux/Features/Home/HomeSlice";
import {
  ArrowDownUp,
  ArrowRightLeft,
  ChevronDown,
  ChevronRight,
  Contrast,
  ImageOff,
  Link2,
  Menu,
  MousePointer,
  Palette,
  PersonStanding,
  RefreshCcw,
  SkipForward,
  Volume2,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Link as ScrollLink } from "react-scroll";
import { ECitizenRead } from "../../Interface/Api/ECitizenInterface";
import HeaderLogo from "../../assets/newRevenueLogo.svg";
import SearchDialog from "./components/SearchDialog";
import SearchInput from "./components/SearchInput";
import { NavBarItems } from "./constants";
import { INavBarItem } from "./interface";

const Navbar = () => {
  const { i18n, t: _ } = useTranslation();
  const { lang, setLang, setApiLang, apiLang } = useLanguageContext();
  const {
    rootFontSize,
    increaseRootFontSize,
    decreaseRootFontSize,
    defaultRootFontSize,
  } = useCustomFontSize();

  const { revenueLogo, emblemLogo } = useLogo();


  const [changeLang, setChangeLang] = useState("मराठी");
  const [isOpen, setIsOpen] = useState(false);
  const [openItems, setOpenItems] = useState<string[]>([]);
  const [openSearchDialog, setOpenSearchDialog] = useState(false);
  const [searchedString, setSearchedString] = useState("");
  const [bigCursorEnabled, setBigCursorEnabled] = useState(false);
  const [isInverted, setIsInverted] = useState(false);
  const [saturation, setSaturation] = useState<"normal" | "grayscale" | "high">(
    "normal"
  );
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [imagesHidden, setImagesHidden] = useState(false);
  const [textSpacingIncreased, setTextSpacingIncreased] = useState(false);
  const [lineHeightIncreased, setLineHeightIncreased] = useState(false);
  const { pathname } = useLocation();

  const navigate = useNavigate();

  const eCitizenQueryRes = useGetECitizenReadQuery(apiLang);
  const [searchByTextMutate, searchByTextRes] = useLazyGetSearchByTextQuery();
  const [hasSearched, setHasSearched] = useState(false);
  const [isHighlightActive, setIsHighlightActive] = useState(false);

  const isActive = useCallback(
    (href: string) => {
      return pathname === href;
    },
    [pathname]
  );

  const isActiveParent = useCallback(
    (items: INavBarItem[]) => {
      return items.some(
        (item) => item.route && isActive(item.route.toString())
      );
    },
    [isActive]
  );

  const navBarItems: [ECitizenRead[]] = [eCitizenQueryRes.data?.items ?? []];

  const handleLangChange = (value: string) => {
    setLang(value);
    setApiLang(value === "en" ? "0" : "1");
    i18n.changeLanguage(value);
  };

  const toggleItem = (title: string) => {
    setOpenItems((prevOpenItems) =>
      prevOpenItems.includes(title)
        ? prevOpenItems.filter((item) => item !== title)
        : [...prevOpenItems, title]
    );
  };

  const isItemOpen = (title: string) => openItems.includes(title);

  useEffect(() => {
    if (bigCursorEnabled) {
      document.body.classList.add("big-cursor-active");
    } else {
      document.body.classList.remove("big-cursor-active");
    }
  }, [bigCursorEnabled]);

  useEffect(() => {
    const allLinks = document.querySelectorAll("a");

    if (isHighlightActive) {
      allLinks.forEach((link) => link.classList.add("highlight-link"));
    } else {
      allLinks.forEach((link) => link.classList.remove("highlight-link"));
    }
    const htmlEl = document.documentElement;

    htmlEl.classList.toggle("invert-colors", isInverted);

    htmlEl.classList.remove("desaturate", "high-saturation");

    if (saturation === "grayscale") {
      htmlEl.classList.add("desaturate");
    } else if (saturation === "high") {
      htmlEl.classList.add("high-saturation");
    }

    htmlEl.classList.toggle("hide-images", imagesHidden);
    htmlEl.classList.toggle("text-spacing-increased", textSpacingIncreased);
    htmlEl.classList.toggle("line-height-increased", lineHeightIncreased);

    return () => {
      allLinks.forEach((link) => link.classList.remove("highlight-link"));
      htmlEl.classList.remove(
        "invert-colors",
        "desaturate",
        "high-saturation",
        "text-spacing-increased",
        "line-height-increased"
      );
    };
  }, [
    isHighlightActive,
    isInverted,
    saturation,
    imagesHidden,
    textSpacingIncreased,
    lineHeightIncreased,
    pathname,
  ]);

  // const textToSpeech = () => {
  //   let text = document.body.innerText;
  //   const voice = new SpeechSynthesisUtterance(text);
  //   window.speechSynthesis.speak(voice);
  // };

  const Accessibility = [
    {
      category: _("accessibility_option_category1"),
      accessibilityButtons: [
        {
          icon: <ZoomIn size={25} aria-hidden="true" />,
          title: _("accessibility_option_category1.title1"),
          onClick: () => {
            if (rootFontSize < 18) {
              increaseRootFontSize();
            }
          },
        },
        {
          icon: <ZoomOut size={25} aria-hidden="true" />,
          title: _("accessibility_option_category1.title2"),
          onClick: () => {
            if (rootFontSize > 14) {
              decreaseRootFontSize();
            }
          },
        },
        {
          icon: <RefreshCcw size={25} aria-hidden="true" />,
          title: _("accessibility_option_category1.title3"),
          onClick: () => {
            defaultRootFontSize();
          },
        },
        {
          icon: <ArrowRightLeft size={25} aria-hidden="true" />,
          title: textSpacingIncreased ? _("accessibility_option_category1.title4") : _("accessibility_option_category1.title5"),
          onClick: () => setTextSpacingIncreased((prev) => !prev),
          isActive: textSpacingIncreased,
        },
        {
          icon: <ArrowDownUp size={25} aria-hidden="true" />,
          title: lineHeightIncreased
            ? _("accessibility_option_category1.title6")
            : _("accessibility_option_category1.title7"),
          onClick: () => setLineHeightIncreased((prev) => !prev),
          isActive: lineHeightIncreased,
        },
      ],
    },
    {
      category: _("accessibility_option_category2"),
      accessibilityButtons: [
        {
          icon: <Link2 size={25} aria-hidden="true" />,
          title: isHighlightActive ? _("accessibility_option_category2.title1") : _("accessibility_option_category2.title2"),
          onClick: () => {
            setIsHighlightActive((prev) => !prev);
          },
          isActive: isHighlightActive,
        },
        {
          icon: <Contrast size={25} aria-hidden="true" />,
          title: isInverted ? _("accessibility_option_category2.title3") : _("accessibility_option_category2.title4"),
          onClick: () => setIsInverted((prev) => !prev),
          isActive: isInverted,
        },
        {
          icon: <Palette size={25} aria-hidden="true" />,
          title: _("accessibility_option_category2.title5"),
          onClick: () => setSaturation("grayscale"),
          isActive: saturation === "grayscale",
        },
        {
          icon: <Palette size={25} aria-hidden="true" />,
          title: _("accessibility_option_category2.title6"),
          onClick: () => setSaturation("high"),
          isActive: saturation === "high",
        },
        {
          icon: <Palette size={25} aria-hidden="true" />,
          title: _("accessibility_option_category2.title7"),
          onClick: () => setSaturation("normal"),
          isActive: saturation === "normal",
        },
      ],
    },
    {
      category: _("accessibility_option_category3"),
      accessibilityButtons: [
        {
          icon: <Volume2 size={25} aria-hidden="true" />,
          title: _("accessibility_option_category3.title1"),
          // onClick: () => textToSpeech(),
          onClick: () => navigate("/screen_reader"),
        },
        {
          icon: <MousePointer size={25} aria-hidden="true" />,
          title: _("accessibility_option_category3.title2"),
          onClick: () => {
            setBigCursorEnabled((prev) => !prev);
          },
        },
        {
          icon: <ImageOff size={25} aria-hidden="true" />,
          title: imagesHidden ? _("accessibility_option_category3.title3") : _("accessibility_option_category3.title4"),
          onClick: () => setImagesHidden((prev) => !prev),
          isActive: imagesHidden,
        },
      ],
    },
  ];

  const NavItems = useCallback(
    () => (
      <div className="hidden lg:block">
        <Menubar className="bg-transparent border-none gap-3">
          {NavBarItems(...navBarItems)?.map((item, index) => (
            <MenubarMenu key={index}>
              {!item.subItem ? (
                <MenubarTrigger asChild>
                  <Link
                    to={_(item.route)}
                    className={cn(
                      "h-9 px-3 bg-transparent text-white text-sm cursor-pointer flex items-center",
                      isActive(_(item.route)) && "bg-white text-black"
                    )}
                    aria-current={isActive(_(item.route)) ? "page" : undefined}
                  >
                    {typeof item.label === "string"
                      ? _(item.label)
                      : item.label}
                  </Link>
                </MenubarTrigger>
              ) : (
                <>
                  <MenubarTrigger
                    className={cn(
                      "h-9 px-3 bg-transparent text-white text-sm cursor-pointer",
                      item.subItem &&
                      isActiveParent(item.subItem) &&
                      "bg-white text-black"
                    )}
                  >
                    {typeof item.label === "string"
                      ? _(item.label)
                      : item.label}
                    <ChevronDown aria-hidden="true" />
                  </MenubarTrigger>
                  <MenubarContent aria-label={`${item.label} submenu`}>
                    <ul className="grid gap-1 p-2 w-80">
                      {item.subItem?.map((subItem) => (
                        <li key={subItem.id}>
                          <Link to={_(subItem.route)} state={{ subItem }}>
                            <MenubarItem
                              className={cn(
                                "block select-none rounded-md p-3 text-sm leading-none no-underline outline-none hover:bg-theme-light-green hover:text-white",
                                isActive(subItem.route.toString()) &&
                                "bg-theme-light-green text-white"
                              )}
                              aria-current={
                                isActive(subItem.route.toString())
                                  ? "page"
                                  : undefined
                              }
                            >
                              {_(subItem.label)}
                            </MenubarItem>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </MenubarContent>
                </>
              )}
            </MenubarMenu>
          ))}
        </Menubar>
      </div>
    ),
    [pathname, navigate, navBarItems, _]
  );

  const handleSearch = (query: string) => {
    if (query.trim() === "") {
      searchByTextRes.reset();
      setHasSearched(false);
      return;
    }
    if (query.length) {
      setOpenSearchDialog(true);
    }
    setSearchedString(query);
    setHasSearched(true);
    searchByTextMutate(query);
  };

  const handleDialogChange = (isOpen: boolean) => {
    setOpenSearchDialog(isOpen);
    if (!isOpen) {
      searchByTextRes.reset();
      setHasSearched(false);
    }
  };

  return (
    <>
      <Helmet>
        <html lang={lang} />
      </Helmet>
      {bigCursorEnabled && <BigCursor />}
      <header role="banner">
        <div className="sm:bg-theme-green bg-[#cbcbcb] sm:text-white border-b">
          <div className="container flex items-center justify-center px-4 py-2 text-sm md:px-8 sm:justify-between">
            <div className="flex items-center gap-5">
              <span className="hidden font-medium tracking-wide sm:inline">
                <a
                  target="_blank"
                  href="https://www.maharashtra.gov.in/"
                  rel="noopener noreferrer"
                  aria-label="Opens in a new tab"
                >
                  महाराष्ट्र शासन <span className="mx-1">|</span> Government of Maharashtra
                </a>
              </span>
            </div>
            <div className="flex items-center justify-center gap-4">
              <SearchInput
                onSearch={handleSearch}
                isLoading={searchByTextRes.isLoading}
                placeholder="Type to search or use voice.."
                className="rounded-md cursor-pointer text-black"
                aria-label="Search the site"
              />
              <ScrollLink
                to="main-content"
                href=""
                title={_("skip_button")}
                className="p-2 text-black bg-white rounded-md cursor-pointer hover:bg-black hover:text-white"
              >
                <SkipForward size={15} />
              </ScrollLink>
              <Popover>
                <PopoverTrigger
                  title={_("accessibility_button")}
                  className="h-8 inline-flex items-center justify-center whitespace-nowrap bg-white sm:text-black hover:bg-black hover:text-white px-3 rounded-md"
                >
                  <PersonStanding size={18} aria-hidden="true" />
                </PopoverTrigger>
                <PopoverContent className="w-full">
                  {Accessibility.map((itm, idx) => (
                    <div className="py-2" key={idx}>
                      <p className="text-sm pb-1">{itm.category}</p>
                      <div className="grid grid-cols-3 gap-2">
                        {itm.accessibilityButtons.map((buttons, id) => (
                          <button
                            key={id}
                            className="flex flex-col items-center p-2 text-center text-black rounded-md cursor-pointer border border-black hover:bg-zinc-100 bg-zinc-200"
                            onClick={buttons.onClick}
                            aria-pressed={buttons.isActive ? "true" : "false"}
                            aria-label={buttons.title}
                          >
                            {buttons.icon}
                            <span className="text-xs font-normal my-1">
                              {buttons.title}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </PopoverContent>
              </Popover>
              <DropdownMenu>
                <DropdownMenuTrigger
                  asChild
                  className="h-8 flex items-center px-2"
                  title={_("changeLanguage_button")}
                >
                  <button
                    className="text-black bg-white rounded-md hover:bg-black hover:text-white hover:cursor-pointer"
                    aria-label="Change language"
                    aria-haspopup="menu"
                    aria-expanded={langMenuOpen}
                    onClick={() => setLangMenuOpen((prev) => !prev)}
                  >
                    {changeLang}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-36">
                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      onClick={() => {
                        handleLangChange("mr");
                        setChangeLang("मराठी");
                      }}
                    >
                      मराठी
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        handleLangChange("en");
                        setChangeLang("English");
                      }}
                    >
                      English
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
        <div className="hidden py-4 bg-white sm:block">
          <div className="container flex items-center justify-between gap-4 px-4 mx-auto md:px-8">
            {/* Left Section */}
            <div className="flex items-center gap-4">
              <img
                src={revenueLogo}
                alt={""}
                // src={HeaderLogo}
                // alt="Revenue Department Government Of Maharashtra"
                className="w-auto h-20"
              />
              <div
                className="text-center cursor-pointer sm:text-left"
                onClick={() => navigate("/")}
                role="button"
              >
                <h1 className="text-xl font-semibold">महसूल विभाग</h1>
                <p className="text-lg font-bold">Revenue Department</p>
                <p>Government of Maharashtra</p>
              </div>
            </div>

            {/* Right Section */}
            <div>
              <img src={emblemLogo} alt={""} className="w-auto h-20" />
              {/* <img src={Emblem} alt="Emblem" className="w-auto h-20" /> */}
            </div>
          </div>
        </div>

        <div className="py-4 bg-[#cbcbcb] border-t border-gray-400 sm:hidden block">
          <div className="flex items-center justify-center gap-5">
            <span className="text-sm font-semibold tracking-wide">
              <a target="_blank" href="https://www.maharashtra.gov.in/">
                महाराष्ट्र शासन
              </a>
            </span>
            <span className="text-sm font-semibold tracking-wide">
              <a target="_blank" href="https://www.maharashtra.gov.in/">
                Government of Maharashtra
              </a>
            </span>
          </div>
        </div>
      </header>
      {/* Navigation */}
      <nav
        className="bg-theme-green"
        role="navigation"
        aria-label="Primary Navigation"
      >
        <div className="container flex items-center justify-between px-4 py-4 mx-auto md:px-8">
          {/* Mobile Menu */}
          <div className="block sm:hidden">
            <div className="container flex items-center justify-between gap-4 px-4 mx-auto md:px-8">
              {/* Left Section */}
              <div className="flex items-center gap-4">
                <img
                  src={HeaderLogo}
                  alt="Revenue Department"
                  className="w-auto h-12"
                />
                <div
                  className="cursor-pointer sm:text-left"
                  onClick={() => navigate("/")}
                  role="button"
                >
                  <h1 className="text-xs font-light text-white">
                    महसूल विभाग
                  </h1>
                  <p className="text-base font-normal text-white">
                    Revenue Department
                  </p>
                  <p className="text-xs font-normal text-white">
                    Government of Maharashtra
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="block lg:hidden">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="lg:hidden"
                  aria-label="Menu"
                >
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="w-[300px] sm:w-[400px] bg-theme-green text-white border-none"
              >
                <ScrollArea className="h-full">
                  <div className="my-10 space-y-5">
                    {NavBarItems(...navBarItems)?.map((item, index) => (
                      <div key={index} className="">
                        {!item.subItem ? (
                          <Link
                            to={_(item.route)}
                            className={cn(
                              "flex w-full items-center rounded-md px-3 py-2 text-sm font-medium hover:text-black",
                              isActive(item.route.toString())
                                ? "bg-white text-black"
                                : "hover:bg-accent"
                            )}
                            onClick={() => setIsOpen(false)}
                          >
                            {typeof item.label === "string"
                              ? _(item.label)
                              : item.label}
                          </Link>
                        ) : (
                          <div className="space-y-2">
                            <button
                              onClick={() =>
                                toggleItem(item.label?.toString() ?? "")
                              }
                              className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium rounded-md hover:bg-accent hover:text-black"
                            >
                              {typeof item.label === "string"
                                ? _(item.label)
                                : item.label}
                              {isItemOpen(item.label?.toString() ?? "") ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <ChevronRight className="w-4 h-4" />
                              )}
                            </button>
                            {isItemOpen(item.label?.toString() ?? "") && (
                              <div className="flex flex-col pl-2 ml-4 space-y-2 border-l">
                                {item.subItem?.map((subItem, idx) => (
                                  <div key={idx}>
                                    <Link
                                      to={_(subItem.route)}
                                      className={cn(
                                        "flex w-full items-center rounded-md px-3 py-2 text-sm font-medium hover:text-black",
                                        isActive(subItem.route.toString())
                                          ? "bg-white text-black"
                                          : "hover:bg-accent"
                                      )}
                                      onClick={() => setIsOpen(false)}
                                    >
                                      {_(subItem.label)}
                                    </Link>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </SheetContent>
            </Sheet>
          </div>

          {/* Desktop Menu */}
          <div className="hidden lg:block">
            <div className="flex items-center gap-3 font-medium tracking-wider xl:gap-10">
              <NavItems />
            </div>
          </div>
        </div>
      </nav>

      <Dialog open={openSearchDialog} onOpenChange={handleDialogChange}>
        <SearchDialog
          data={searchByTextRes.data?.items}
          isLoading={searchByTextRes.isLoading}
          isError={searchByTextRes.isError}
          hasSearched={hasSearched}
          searchText={searchedString}
        />
      </Dialog>
    </>
  );
};

export default Navbar;
